import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, tagApi } from '../utils/api';
import { parseFen } from './ChessDiagramGenerator';
import ChessDiagram from '../components/ChessDiagram';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function FenHelpModal({ onClose, prefs }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1050,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        background: 'var(--bs-body-bg)',
        borderRadius: '0.875rem',
        width: '100%',
        maxWidth: 520,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
          <h5 className="mb-0 fw-semibold">What is FEN?</h5>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
        </div>

        {/* Body */}
        <div className="overflow-auto p-4">
          <p>
            <strong>FEN</strong> (Forsyth–Edwards Notation) is a compact text format that
            describes a complete chess position. Each flashcard needs a FEN so the board
            can be drawn correctly.
          </p>
          <p>A FEN string has six space-separated fields:</p>
          <ol className="mb-3">
            <li><strong>Piece placement</strong> — ranks 8→1, pieces by letter, digits for empty squares, <code>/</code> between ranks.</li>
            <li><strong>Active color</strong> — <code>w</code> or <code>b</code>.</li>
            <li><strong>Castling rights</strong> — <code>KQkq</code> or <code>-</code>.</li>
            <li><strong>En passant target square</strong> — e.g. <code>e3</code> or <code>-</code>.</li>
            <li><strong>Halfmove clock</strong> — moves since last capture or pawn advance.</li>
            <li><strong>Fullmove number</strong> — starts at 1.</li>
          </ol>

          <p className="fw-semibold mb-2">Starting position</p>
          <div className="d-flex justify-content-center mb-3">
            <ChessDiagram fen={STARTING_FEN} prefs={prefs} size={240} />
          </div>
          <div className="text-center">
            <code className="user-select-all" style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
              {STARTING_FEN}
            </code>
          </div>

          <hr />
          <p className="text-muted small mb-0">
            Tip: most chess software (Lichess, Chess.com, ChessBase, HIARCS, etc.) can copy a
            position's FEN to your clipboard.
          </p><br/>
          <p className="text-muted small mb-0">
            ChessFlashcards.org only needs the first two parts of the FEN.
            (Where the pieces are and who's move it is.)
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-top d-flex justify-content-end">
          <button type="button" className="btn btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

const EMPTY = {
  name: '', fen: '', flipped: false, showSideToMoveDisc: false,
  problem: '', problem_rows: 3,
  solution: '', solution_rows: 3,
};

export default function FlashcardEditPage() {
  const { id }          = useParams();   // undefined when creating
  const isNew           = !id;
  const { token, prefs } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]             = useState(EMPTY);
  const [fenError, setFenError]     = useState('');
  const [error, setError]           = useState('');
  const [busy, setBusy]             = useState(false);
  const [showFenHelp, setShowFenHelp] = useState(false);
  const [userTags, setUserTags]         = useState([]);       // all tags the user has defined
  const [selectedTagIds, setSelectedTagIds] = useState(new Set()); // currently selected
  const [initialTagIds, setInitialTagIds]   = useState(new Set()); // tags at load time

  // Load user's tag library
  useEffect(() => {
    tagApi.list(token).then(setUserTags).catch(() => {});
  }, [token]);

  // Load card data (and its current tags) when editing
  useEffect(() => {
    if (!isNew) {
      flashcardApi.get(id, token)
        .then(card => setForm({
          name:                card.name                ?? '',
          fen:                 card.fen                 ?? '',
          flipped:             card.flipped             ?? false,
          showSideToMoveDisc:  card.show_side_to_move ?? false,
          problem:             card.problem             ?? '',
          problem_rows:        card.problem_rows        || 3,
          solution:            card.solution            ?? '',
          solution_rows:       card.solution_rows       || 3,
        }))
        .catch(err => setError(err.message));
      tagApi.listForCard(id, token)
        .then(tags => {
          const ids = new Set((tags || []).map(t => t.id));
          setSelectedTagIds(ids);
          setInitialTagIds(ids);
        })
        .catch(() => {});
    }
  }, [id, isNew, token]);

  const toggleTag = (tagId) => {
    setSelectedTagIds(prev => {
      const next = new Set(prev);
      next.has(tagId) ? next.delete(tagId) : next.add(tagId);
      return next;
    });
  };

  // Sync tag selections to the backend for a given cardId.
  const syncTags = async (cardId, prevIds) => {
    const toAdd    = [...selectedTagIds].filter(tid => !prevIds.has(tid));
    const toRemove = [...prevIds].filter(tid => !selectedTagIds.has(tid));
    await Promise.all([
      ...toAdd.map(tid    => tagApi.addToCard(cardId, tid, token)),
      ...toRemove.map(tid => tagApi.removeFromCard(cardId, tid, token)),
    ]);
  };

  const handle = e => {
    const { name, value, type, checked } = e.target;
    if (name === 'fen') setFenError('');
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value),
    }));
  };

  const validateFen = (fen) => {
    try {
      parseFen(fen);
      return null;
    } catch (e) {
      return e.message;
    }
  };

  const submit = async e => {
    e.preventDefault();
    setError('');
    const fenErr = validateFen(form.fen);
    if (fenErr) {
      setFenError(fenErr);
      return;
    }
    const { activeColor } = parseFen(form.fen);
    const { showSideToMoveDisc: _disc, ...formRest } = form;
    const payload = {
      ...formRest,
      show_side_to_move: form.showSideToMoveDisc,
      to_move: activeColor === 'b' ? 'B' : 'W',
    };
    setBusy(true);
    try {
      const saved = isNew
        ? await flashcardApi.create(payload, token)
        : await flashcardApi.update(id, payload, token);
      const cardId = isNew ? saved.id : Number(id);
      await syncTags(cardId, initialTagIds);
      if (isNew) navigate(`/flashcards/${saved.id}`); else navigate(-1);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">{isNew ? 'New Flashcard' : 'Edit Flashcard'}</h3>
          <button className="btn btn-link pe-0" onClick={() => navigate('/dashboard')}>
            Cancel
          </button>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={submit}>
          <div className="card shadow-sm">
            <div className="card-body">

              <div className="mb-3">
                <label className="form-label fw-semibold">Name <span className="text-danger">*</span></label>
                <input type="text" name="name" className="form-control"
                  value={form.name} onChange={handle} required />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                  FEN <span className="text-danger">*</span>
                  <button
                    type="button"
                    onClick={() => setShowFenHelp(true)}
                    title="What is FEN?"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 18, height: 18, borderRadius: '50%',
                      border: '1.5px solid var(--bs-secondary)',
                      background: 'transparent',
                      color: 'var(--bs-secondary)',
                      fontSize: '0.7rem', fontWeight: 700,
                      lineHeight: 1, padding: 0, cursor: 'pointer',
                    }}
                  >?</button>
                </label>
                <input
                  type="text"
                  name="fen"
                  className={`form-control font-monospace${fenError ? ' is-invalid' : ''}`}
                  value={form.fen}
                  onChange={handle}
                  required
                  placeholder="e.g. rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                />
                {fenError && <div className="invalid-feedback">{fenError}</div>}
              </div>

              <div className="mb-2 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="flipped"
                  name="flipped"
                  checked={form.flipped}
                  onChange={handle}
                />
                <label className="form-check-label" htmlFor="flipped">
                  Show from Black's perspective?
                </label>
              </div>

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="showSideToMoveDisc"
                  name="showSideToMoveDisc"
                  checked={form.showSideToMoveDisc}
                  onChange={handle}
                />
                <label className="form-check-label" htmlFor="showSideToMoveDisc">
                  Show side-to-move disc
                </label>
              </div>

              {form.fen && (
                <div className="mb-3">
                  <ChessDiagram
                    fen={form.fen}
                    prefs={{
                      ...prefs,
                      diagramFlipped: form.flipped,
                      diagramShowSideToMoveDisc: form.showSideToMoveDisc,
                    }}
                    size={320}
                  />
                </div>
              )}

              {/* ── Tags ──────────────────────────────────────────────── */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Tags</label>
                {userTags.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {userTags.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        className={`btn btn-sm ${selectedTagIds.has(t.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => toggleTag(t.id)}
                      >
                        {t.tag}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small mb-0">
                    No tags yet — add some in <a href="/preferences">Preferences</a>.
                  </p>
                )}
              </div>

              <hr />

              <div className="mb-3">
                <label className="form-label fw-semibold">Problem</label>
                <textarea name="problem" className="form-control"
                  rows={form.problem_rows} value={form.problem} onChange={handle} />
                <div className="d-flex align-items-center gap-2 mt-1">
                  <small className="text-muted">Rows:</small>
                  <input type="number" name="problem_rows" className="form-control form-control-sm"
                    style={{ width: 60 }} min={1} max={20}
                    value={form.problem_rows} onChange={handle} />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Solution</label>
                <textarea name="solution" className="form-control"
                  rows={form.solution_rows} value={form.solution} onChange={handle} />
                <div className="d-flex align-items-center gap-2 mt-1">
                  <small className="text-muted">Rows:</small>
                  <input type="number" name="solution_rows" className="form-control form-control-sm"
                    style={{ width: 60 }} min={1} max={20}
                    value={form.solution_rows} onChange={handle} />
                </div>
              </div>

            </div>
            <div className="card-footer bg-transparent d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary"
                onClick={() => navigate('/dashboard')}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save Flashcard'}
              </button>
            </div>
          </div>
        </form>

      {showFenHelp && (
        <FenHelpModal prefs={prefs} onClose={() => setShowFenHelp(false)} />
      )}
    </div>
  );
}
